import { styled } from '@mui/material/styles';
import { MarketingAssetSchema } from '@/lib/types';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import MarketingAssetsFilters from './MarketingAssetsFilters';
import { RefObject, useEffect, useRef, useState } from 'react';
import { Mode } from '@/components/FileExplorer/FileExplorerHelpers';
import FileExplorer from '@/components/FileExplorer/FileExplorer';
import MarketingAssetsUploadModal from '@/components/MarketingAssetsUploadModal/MarketingAssetsUploadModal';
import { DropZone } from '@/components/DropZone/DropZone';
import {
  fetchMarketingAssets,
  fetchMarketingAssetsByFolder,
  fetchMarketingAssetsFolderStructure,
} from '@/lib/api';
import { useFileExplorerData } from '@/hooks/useFileExplorerData';
import { assetQueryKeys } from '@/lib/assetQueryKeys';
import Button from '@/components/Button/Button';
import { FormattedMessage } from 'react-intl';
import UploadIcon from '@/assets/upload.svg?react';
import FilterIcon from '@/assets/filter.svg?react';
import Popper from '@/components/Popper/Popper';
import { flattenDeep } from 'lodash';
import { useMarketingAssetBulkActions } from '@/hooks/useMarketingAssetBulkActions';
import RemoveFilesConfirmModal from '@/components/RemoveFilesConfirmModal/RemoveFilesConfirmModal';

const MarketingAssetsTable = ({
  gameId,
  gameGroup,
  filters,
  onFiltersChange,
  scrollContainerRef,
}: {
  gameId: number;
  gameGroup?: string | null;
  filters: Partial<FilterSchema>;
  onFiltersChange: (filters: Partial<FilterSchema>) => void;
  scrollContainerRef?: RefObject<HTMLElement | null>;
}) => {
  const [path, setPath] = useState<string[]>([]);
  const [currentView, setCurrentView] = useState<Mode>('grid');
  const filterRef = useRef<HTMLButtonElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);

  const {
    folderStructure,
    filesData,
    filesFlatData,
    loading,
    loadingNextPage,
    refetch,
  } = useFileExplorerData({
    filters,
    path,
    gameIds: [gameId],
    gameGroup,
    scrollContainerRef:
      scrollContainerRef as unknown as RefObject<HTMLElement>,
    folderStructureFn: fetchMarketingAssetsFolderStructure,
    folderStructureQueryKey:
      assetQueryKeys.marketing_assets.folderStructure,
    fetchFilesByFolderFn: fetchMarketingAssetsByFolder,
    fetchFilesByFolderQueryKey: assetQueryKeys.marketing_assets.byFolder,
    fetchFilesFlatFn: fetchMarketingAssets,
    fetchFilesFlatQueryKey: assetQueryKeys.marketing_assets.flat,
  });

  const showNotification =
    flattenDeep<
      | string
      | number
      | boolean
      | string[]
      | number[]
      | [string, string]
    >(Object.values(filters || {})).length > 0;

  const handleUploadClick = () => {
    setUploadModalOpen(true);
  };

  const handleFileChange = (files: FileList | null) => {
    if (files && files.length > 0) {
      setDroppedFile(files[0]);
      setUploadModalOpen(true);
    }
  };

  const {
    selected,
    setSelected,
    handleDownloadClick,
    handleRemoveClick,
    handleRemoveCancel,
    handleRemoveConfirm,
    removeModalOpen,
    downloadLoading,
    removeLoading,
  } = useMarketingAssetBulkActions({
    filesData: filesData as MarketingAssetSchema[] | undefined,
    filesFlatData: filesFlatData as MarketingAssetSchema[] | undefined,
    onAfterMutation: refetch,
  });

  useEffect(() => {
    setSelected([]);
  }, [path, currentView, setSelected]);

  const handleFileDelete = () => {
    refetch();
  };

  return (
    <>
      <FileExplorerWrapper>
        <DropZone onFileChange={handleFileChange}>
          <FileExplorer
            folderStructure={folderStructure}
            files={filesData as MarketingAssetSchema[]}
            filesFlatList={filesFlatData as MarketingAssetSchema[]}
            loading={loading}
            path={path}
            onPathChange={setPath}
            type="marketing"
            onFileDelete={handleFileDelete}
            showHeader={true}
            selected={selected}
            onSelect={setSelected}
            onDownloadClick={handleDownloadClick}
            onRemoveClick={handleRemoveClick}
            showDownload={true}
            showRemove={true}
            downloadLoading={downloadLoading}
            removeLoading={removeLoading}
            currentView={currentView}
            onViewChange={setCurrentView}
            hiddenRoot="marketing_assets"
            loadingNextPage={loadingNextPage}
            actions={
              <>
                <Button
                  onClick={handleUploadClick}
                  variant="contained"
                  aria-label="Upload Button"
                >
                  <UploadIcon />
                  <FormattedMessage
                    id="upload"
                    defaultMessage="Upload"
                  />
                </Button>
                <Button
                  variant="outlined"
                  icon
                  ref={filterRef}
                  onClick={() => setFiltersOpen((v) => !v)}
                  aria-label="Filter Button"
                >
                  <FilterIcon />
                  {showNotification && <FilterNotification />}
                </Button>
                <Popper
                  aria-label="Filters Popper"
                  open={filtersOpen}
                  anchorEl={filterRef.current}
                  onClose={() => setFiltersOpen(false)}
                  offsetY={10}
                >
                  <MarketingAssetsFilters
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                  />
                </Popper>
              </>
            }
          />
        </DropZone>
      </FileExplorerWrapper>
      <RemoveFilesConfirmModal
        open={removeModalOpen}
        count={selected.length}
        loading={removeLoading}
        onCancel={handleRemoveCancel}
        onConfirm={handleRemoveConfirm}
      />
      <MarketingAssetsUploadModal
        gameId={gameId}
        gameGroup={gameGroup}
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setDroppedFile(null);
        }}
        onSave={() => {
          setDroppedFile(null);
        }}
        initialFile={droppedFile}
      />
    </>
  );
};

const FileExplorerWrapper = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(2)} 40px 0`,
}));

const FilterNotification = styled('div')(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  position: 'absolute',
  top: -4,
  right: -4,
}));

export default MarketingAssetsTable;
